// API service for GoldCalc
// Uses backend endpoints that fetch data from free public sources (LBMA, exchangerate.host)

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Scenario rates - fixed values
export let SCENARIO_RATES = {
  conservative: 0.28,
  base: 0.36,
  optimistic: 0.54
};

export const SCENARIOS = [
  { value: 'conservative', label: 'Conservative', rate: '28%/year' },
  { value: 'base', label: 'Base', rate: '36%/year' },
  { value: 'optimistic', label: 'Optimistic', rate: '54%/year' }
];

/**
 * Fetch dynamic scenario rates from backend
 */
export const fetchScenarioRates = async () => {
  try {
    const response = await fetch(`${API}/scenario-rates`);
    if (!response.ok) throw new Error('Failed to fetch scenario rates');
    const data = await response.json();
    if (data.rates) {
      SCENARIO_RATES = data.rates;
      // Update SCENARIOS labels with actual rates
      SCENARIOS[0].rate = `${(data.rates.conservative * 100).toFixed(1)}%/year`;
      SCENARIOS[1].rate = `${(data.rates.base * 100).toFixed(1)}%/year`;
      SCENARIOS[2].rate = `${(data.rates.optimistic * 100).toFixed(1)}%/year`;
    }
    return data;
  } catch (error) {
    console.error('Error fetching scenario rates:', error);
    return { rates: SCENARIO_RATES };
  }
};

export const CURRENCIES = [
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' }
];

export const HORIZONS = [
  { value: 0.25, label: '3 months' },
  { value: 0.5, label: '6 months' },
  { value: 1, label: '1 year' },
  { value: 3, label: '3 years' },
  { value: 5, label: '5 years' }
];

/**
 * Fetch current gold spot price
 */
export const fetchGoldPrice = async () => {
  try {
    const response = await fetch(`${API}/gold`);
    if (!response.ok) throw new Error('Failed to fetch gold price');
    const data = await response.json();
    return {
      pricePerGramEUR: data.price_per_gram_eur,
      pricePerOunceUSD: data.price_per_ounce_usd,
      source: data.source,
      isFallback: data.is_fallback,
      lastUpdated: data.last_updated,
      warning: data.warning
    };
  } catch (error) {
    console.error('Error fetching gold price:', error);
    throw error;
  }
};

/**
 * Fetch current FX rates
 */
export const fetchFXRates = async () => {
  try {
    const response = await fetch(`${API}/fx`);
    if (!response.ok) throw new Error('Failed to fetch FX rates');
    const data = await response.json();
    return {
      rates: data.rates,
      source: data.source,
      isFallback: data.is_fallback,
      lastUpdated: data.last_updated,
      warning: data.warning
    };
  } catch (error) {
    console.error('Error fetching FX rates:', error);
    throw error;
  }
};

/**
 * Fetch historical gold price data
 */
export const fetchHistoricalData = async (years = 5) => {
  try {
    const response = await fetch(`${API}/history?years=${years}`);
    if (!response.ok) throw new Error('Failed to fetch historical data');
    const data = await response.json();
    return {
      data: data.data,
      source: data.source,
      isSimulated: data.is_simulated,
      lastUpdated: data.last_updated,
      note: data.note
    };
  } catch (error) {
    console.error('Error fetching historical data:', error);
    throw error;
  }
};

/**
 * Calculate gold investment using backend
 */
export const calculateGoldInvestment = async (amount, currency, horizonYears, scenario) => {
  try {
    const response = await fetch(`${API}/calculate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: parseFloat(amount),
        currency: currency,
        horizon_years: parseFloat(horizonYears),
        scenario: scenario
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Calculation failed');
    }
    
    const data = await response.json();
    return {
      investmentAmount: data.investment_amount,
      currency: CURRENCIES.find(c => c.code === data.currency) || CURRENCIES[0],
      gramsOfGold: data.grams_of_gold.toFixed(4),
      currentValueEUR: data.current_value_eur.toFixed(2),
      horizonLabel: HORIZONS.find(h => h.value === data.horizon_years)?.label || `${data.horizon_years} years`,
      horizonYears: data.horizon_years,
      scenario: SCENARIOS.find(s => s.value === data.scenario) || SCENARIOS[1],
      annualRate: (data.annual_rate * 100).toFixed(0),
      futureValueMin: data.future_value_conservative.toFixed(2),
      futureValueMax: data.future_value_optimistic.toFixed(2),
      futureValueSelected: data.future_value_selected.toFixed(2),
      goldPriceUsed: data.gold_price_per_gram_eur.toFixed(2),
      fxRateUsed: data.fx_rate_used.toFixed(4),
      lastUpdated: new Date().toISOString(),
      source: data.source,
      isFallback: data.is_fallback,
      disclaimer: data.disclaimer
    };
  } catch (error) {
    console.error('Error calculating gold investment:', error);
    throw error;
  }
};

/**
 * Get combined market data (gold price + FX rates)
 */
export const fetchMarketData = async () => {
  try {
    const [goldData, fxData] = await Promise.all([
      fetchGoldPrice(),
      fetchFXRates()
    ]);
    
    return {
      goldPrice: goldData,
      fxRates: fxData,
      lastUpdated: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error fetching market data:', error);
    throw error;
  }
};
